/**
 * 落地页创建流：prompt（localStorage）+ 附件（sessionStorage）跨登录/刷新恢复。
 * 与 index1 HeroPromptInput、CtaV3 共用。
 */
export const PROMPT_CACHE_KEY = 'create:lastPrompt';
export const CHAT_STATE_CACHE_KEY = 'create:lastChatAttachments';

export type CreateChatStateSnapshot = {
  assets: Array<Record<string, unknown>>;
  documents: Array<Record<string, unknown>>;
};

const isPersistableUrl = (url: unknown): boolean =>
  typeof url === 'string' && url.length > 0 && !url.startsWith('blob:');

/** 仅缓存已上传完成、URL 可复用的条目（排除 loading 与 blob 临时预览） */
export function stripChatStateForCache(state: CreateChatStateSnapshot): CreateChatStateSnapshot {
  return {
    assets: (state.assets || [])
      .filter((a) => !a.loading && isPersistableUrl(a.url ?? a.cdn_url))
      .map((a) => ({ ...a })),
    documents: (state.documents || []).filter((d) => !d.loading).map((d) => ({ ...d })),
  };
}

export function loadCreateChatStateCache(): CreateChatStateSnapshot | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CHAT_STATE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CreateChatStateSnapshot;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      assets: Array.isArray(parsed.assets) ? parsed.assets : [],
      documents: Array.isArray(parsed.documents) ? parsed.documents : [],
    };
  } catch {
    return null;
  }
}

export function saveCreateChatStateCache(state: CreateChatStateSnapshot): void {
  if (typeof sessionStorage === 'undefined') return;
  const payload = stripChatStateForCache(state);
  if (payload.assets.length === 0 && payload.documents.length === 0) {
    sessionStorage.removeItem(CHAT_STATE_CACHE_KEY);
    return;
  }
  try {
    sessionStorage.setItem(CHAT_STATE_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage 配额满时静默失败，不影响主流程
  }
}

export function clearCreateChatStateCache(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(CHAT_STATE_CACHE_KEY);
}

/** 与 workspace CreateChat 一致：把附件序列化进 prompt */
export function serializeChatStateAttachments(
  state: CreateChatStateSnapshot,
  currentPrompt: string
): string {
  let result = '';
  const assets = state.assets || [];
  for (const asset of assets) {
    if (asset.loading) continue;
    if (currentPrompt && currentPrompt.includes(String(asset.id))) continue;
    const url = (asset.url || asset.cdn_url || '') as string;
    if (!url) continue;
    const w = (asset.width as number) || 128;
    const h = (asset.height as number) || 128;
    const isMaterial = asset.isMaterial !== false;
    if (isMaterial) {
      result += `\n@VeesoMaterial(${JSON.stringify({
        id: asset.id,
        filename: asset.filename || asset.name || '',
        url: ` \`${url}\` `,
        width: w,
        height: h,
        aspectRatio: Math.round((100 * w) / h) / 100,
        tag: asset.tag || '',
      })})`;
    } else {
      result += `\n@VeesoMaterial(${JSON.stringify({
        id: asset.id,
        url: ` \`${url}\` `,
        type: 'reference',
        width: w,
        height: h,
        aspectRatio: Math.round((100 * w) / h) / 100,
        tag: asset.tag || 'Background',
      })})`;
    }
  }
  const docs = state.documents || [];
  for (const doc of docs) {
    if (doc.loading) continue;
    if (currentPrompt && currentPrompt.includes(String(doc.id))) continue;
    result += `\n@VeesoDocument(${JSON.stringify({
      filename: doc.filename || doc.name || '',
      id: doc.id,
    })})`;
  }
  return result;
}
